import React from 'react';

import { DefaultContext } from '@apollo/client';
import { isNil } from 'es-toolkit';

import { mergeObjects } from '@hrlogics/helper-utils';

import { Nullable } from '@clearverify/types';

import {
  PeoPayrollProviderByIdDocument,
  PeoPayrollProviderByIdQuery,
  PeoPayrollProviderByIdQueryVariables,
  PeoPayrollProviderCompanyType,
  PeoPayrollProviderUpdate,
  usePeoPayrollProviderByIdQuery,
  usePeoPayrollProviderUpdateMutation,
} from 'src/api';
import { useFileUpload } from 'src/hooks/file';
import { DroppedFile } from 'src/types/file';

import { useNotification } from '../app';

export const usePeoPayrollProviderById = (peoPayrollProviderId: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { uploadFile, uploading } = useFileUpload();

  const { data, loading } = usePeoPayrollProviderByIdQuery({
    variables: {
      id: String(peoPayrollProviderId),
    },
    skip: !peoPayrollProviderId,
  });

  const peoPayrollProvider = React.useMemo(
    () => data?.peoPayrollProvider,
    [data?.peoPayrollProvider],
  );

  const [peoPayrollProviderUpdateMutation, { loading: updating }] =
    usePeoPayrollProviderUpdateMutation({
      context: createApolloContext({
        errorMessage: "Can't update Company Information",
        successMessage: 'Company Information has been successfully updated',
      }),
      onCompleted: (data, clientOptions) => {
        if (!peoPayrollProviderId) return;
        const queryOptions = {
          query: PeoPayrollProviderByIdDocument,
          variables: {
            id: peoPayrollProviderId,
          } satisfies PeoPayrollProviderByIdQueryVariables,
        };
        const queryData = clientOptions?.client?.readQuery<
          PeoPayrollProviderByIdQuery,
          PeoPayrollProviderByIdQueryVariables
        >(queryOptions);
        try {
          if (queryData) {
            clientOptions?.client?.writeQuery<
              PeoPayrollProviderByIdQuery,
              PeoPayrollProviderByIdQueryVariables
            >({
              ...queryOptions,
              data: mergeObjects<PeoPayrollProviderByIdQuery>(queryData, {
                peoPayrollProvider: data.peoPayrollProviderUpdate,
              }),
            });
          }
        } catch (error) {
          console.error(error);
        }
      },
    });

  const updatePeoPayrollProvider = React.useCallback(
    async (params: { data: Omit<PeoPayrollProviderUpdate, 'id'>; context?: DefaultContext }) => {
      if (isNil(peoPayrollProviderId)) {
        return;
      }
      const { data, context } = params;

      await peoPayrollProviderUpdateMutation({
        variables: {
          where: { id: peoPayrollProviderId },
          data,
        },
        context,
      });
    },
    [peoPayrollProviderId, peoPayrollProviderUpdateMutation],
  );

  const updatePeoPayrollProviderLogo = React.useCallback(
    async (logoFile: DroppedFile | string) => {
      // TODO: handle case with fileId
      if (typeof logoFile === 'string') throw new Error('Not implemented');
      if (!logoFile.file) return;

      try {
        const uploadedFile = await uploadFile(logoFile.file);
        await updatePeoPayrollProvider({
          data: {
            logo: {
              connect: { id: uploadedFile.id },
            },
          },
        });
      } catch (error) {
        console.error({ error });
      }
    },
    [updatePeoPayrollProvider, uploadFile],
  );

  return {
    peoPayrollProvider,
    peoPayrollProviderId,
    updatePeoPayrollProvider,
    updatePeoPayrollProviderLogo,
    loading,
    updating: updating || uploading,
    isPartnership: peoPayrollProvider?.companyType === PeoPayrollProviderCompanyType.Partnership,
  };
};
